import React, { useState, useEffect, useCallback, useMemo } from "react";
import Table from "../../components/Table/table";
import Loading from "../../components/Loading/loading";
import { Link } from 'react-router-dom';
import { utils } from "react-modern-calendar-datepicker";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";
import "./Miscellaneous.css"; 

const INVENTORY_API = "/api/getmiscellaneousdata";
const CURRENCY_API_URL = "/api/getcurrencypricesdata";

const PRODUCT_TYPES = [
  { key: 'profil', label: 'پروفیل', keywords: ['پروفیل'] },
  { key: 'tirahan', label: 'تیرآهن', keywords: ['تیرآهن'] },
  { key: 'nabshi', label: 'نبشی', keywords: ['نبشی'] },
  { key: 'navdani', label: 'ناودونی', keywords: ['ناودونی'] },
  { key: 'varagh', label: 'ورق', keywords: ['ورق'] },
  { key: 'hash', label: 'هاش', keywords: ['هاش'] },
];

// --- Helper Functions ---
function toFarsiDigits(num) {
  if (num === null || num === undefined) return ""; 
  const farsiDigits = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
  return num.toString().replace(/[0-9]/g, x => farsiDigits[x]);
}

const MiscellaneousInventory = () => {
  // State for all product categories
  const [data, setData] = useState({ 
    hash: [], tirahan: [], profil: [], varagh: [], nabshi: [], navdani: [] 
  });
  const [lastCurrency, setLastCurrency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tracks which Groups (Sizes or Factories) are expanded
  const [sizesShowingUnpriced, setSizesShowingUnpriced] = useState(new Set()); 
  const [selectedDay, setSelectedDay] = useState(utils("fa").getToday()); 

  // --- 1. Helper: Format Date ---
  const formatToAPIDate = (jalaliDate) =>
    `${jalaliDate.year}-${String(jalaliDate.month).padStart(2, "0")}-${String(
      jalaliDate.day
    ).padStart(2, "0")}`;

  // --- 2. Handler: Toggle Unpriced Items ---
  const handleTitleClick = (productType, groupKey) => {
      const key = `${productType}-${groupKey}`;
      setSizesShowingUnpriced(prevSizes => {
          const newSet = new Set(prevSizes);
          if (newSet.has(key)) newSet.delete(key);
          else newSet.add(key);
          return newSet;
      });
  }

  // --- 3. Logic: Process Data (Grouping & Filtering) ---
  const processProducts = useCallback((rawProducts, productType) => {
    if (!rawProducts || rawProducts.length === 0) {
      return { productsBySize: {}, sortedSizes: [], sizeClickability: {} };
    }

    // A. Determine Grouping Strategy
    // Profil/Varagh are grouped by 'Factory', others by 'Size'
    const isFactoryGrouped = ['profil', 'varagh'].includes(productType);

    // B. Normalize Data Fields based on grouping strategy
    const products = rawProducts.map(p => {
        let title, description;

        if (isFactoryGrouped) {
            // For Factory Grouping: Title = Seller, Desc = Origin
            title = p.seller || p.title || "فروشنده نامشخص";
            description = p.originName || "";
        } else {
            // For Size Grouping: Title = Factory, Desc = Origin - Seller
            title = p.factory || p.title || "کارخانه نامشخص";
            description = `${p.originName || ''} - ${p.seller || ''}`.replace(/^- | -$/g, '');
        }

        return { ...p, title, description };
    });

    const sortByTitle = (a, b) => (a.title || "").localeCompare(b.title || "");

    // C. Separate Priced vs Unpriced
    const pricedProducts = products
      .filter((p) => p.price && Number(p.price) !== 0)
      .sort(sortByTitle);
    const unpricedProducts = products
      .filter((p) => !p.price || Number(p.price) === 0)
      .sort(sortByTitle);

    // D. Group Items
    const getGroupKey = (product) => {
        if (isFactoryGrouped) {
            return product.factory || "کارخانه نامشخص";
        }
        return product.size || "بدون سایز";
    };

    const groupItems = (items) =>
      items.reduce((acc, product) => {
        const key = getGroupKey(product);
        if (!acc[key]) acc[key] = [];
        acc[key].push(product);
        return acc;
      }, {});

    const pricedGrouped = groupItems(pricedProducts);
    const unpricedGrouped = groupItems(unpricedProducts);

    // E. Sort Keys (Alphabetical for Factories, Numerical for Sizes)
    const allKeys = new Set([
      ...Object.keys(pricedGrouped),
      ...Object.keys(unpricedGrouped),
    ]);

    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      if (isFactoryGrouped) {
          return a.localeCompare(b, 'fa'); 
      } else {
          if (a === "بدون سایز") return 1;
          if (b === "بدون سایز") return -1;
          return Number(a) - Number(b);
      }
    });
    
    // F. Combine based on Toggle State
    const combinedProductsByKey = {};
    const effectiveKeys = new Set();
    const keyClickability = {};
    
    sortedKeys.forEach((key) => {
      const uniqueId = `${productType}-${key}`;
      const priced = pricedGrouped[key] || [];
      const unpriced = unpricedGrouped[key] || [];
      
      const isUnpricedToggledOn = sizesShowingUnpriced.has(uniqueId);
      const hasPriced = priced.length > 0;
      
      keyClickability[key] = hasPriced; // Clickable only if priced items exist
      
      let finalGroup;
      if (hasPriced) {
          finalGroup = isUnpricedToggledOn ? [...priced, ...unpriced] : priced;
      } else {
          finalGroup = unpriced;
      }

      combinedProductsByKey[key] = finalGroup;
      if (finalGroup.length > 0) effectiveKeys.add(key);
    });

    return { 
        productsBySize: combinedProductsByKey, 
        sortedSizes: sortedKeys.filter(k => effectiveKeys.has(k)),
        sizeClickability: keyClickability 
    };
  }, [sizesShowingUnpriced]);

  // --- 4. Memoize All Data ---
  const allProcessedData = useMemo(() => {
      const result = {};
      PRODUCT_TYPES.forEach(type => {
          result[type.key] = processProducts(data[type.key], type.key);
      });
      return result;
  }, [data, processProducts]);

  // --- 5. Effect: Fetch Currency (Every 60 Seconds) ---
  useEffect(() => {
    const fetchData = () => {
      fetch(CURRENCY_API_URL)
        .then((res) => res.json())
        .then((data) => {
          if (data.length > 0) setLastCurrency(data[data.length - 1]);
        })
        .catch(console.error);
    };

    fetchData(); 
    const interval = setInterval(fetchData, 60_000); 
    return () => clearInterval(interval);
  }, []);

  // --- 6. Effect: Fetch Inventory (Date Change OR Every 60 Seconds) ---
  useEffect(() => {
    if (!selectedDay) return;

    // isBackground = true means "Silent Update" (no spinner)
    const fetchData = async (isBackground = false) => {
      if (!isBackground) setIsLoading(true);
      setError(null);
      
      const formattedDate = formatToAPIDate(selectedDay);
      const urlWithDate = `${INVENTORY_API}?date=${formattedDate}`; 
      
      try {
        const response = await fetch(urlWithDate);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const jsonData = await response.json();
        
        // Initialize empty arrays for all types
        const groupedData = {
            hash: [], tirahan: [], profil: [], varagh: [], nabshi: [], navdani: []
        };
        
        // Helper to sort item into correct category array
        const categorizeItem = (item) => {
             const nameToCheck = (item.name || "").toLowerCase().trim();
             if (!nameToCheck) return; 

             for (const typeDef of PRODUCT_TYPES) {
                 const isMatch = typeDef.keywords.some(keyword => nameToCheck.includes(keyword));
                 if (isMatch) {
                     groupedData[typeDef.key].push(item);
                     return; 
                 }
             }
        };

        // Handle both Array response and Object response
        if (Array.isArray(jsonData)) {
            jsonData.forEach(categorizeItem);
        } else if (typeof jsonData === 'object') {
            Object.values(jsonData).flat().forEach(categorizeItem);
        }

        setData(groupedData);

      } catch (e) {
        console.error("Failed to fetch data:", e);
        // Only block UI with error if it's the user's manual action
        if (!isBackground) {
            setError("Failed to load inventory data for the selected date.");
            setData({ hash: [], tirahan: [], profil: [], varagh: [], nabshi: [], navdani: [] });
        }
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    };

    // A. Initial Load (Shows Spinner)
    fetchData(false);

    // B. Interval Load (Silent - Every 60s)
    const interval = setInterval(() => fetchData(true), 60000);

    // C. Cleanup
    return () => clearInterval(interval);
  }, [selectedDay]);

  // --- 7. Handler: Date Picker ---
  const handleDateChange = (dateObject) => {
    if (!dateObject) return;
    const jalaliDate = {
      year: Number(dateObject.year),
      month: Number(dateObject.month.number || dateObject.month),
      day: Number(dateObject.day),
    };
    setSelectedDay(jalaliDate);
  };
  
  if (isLoading) return <Loading />;
  
  // Check if all data arrays are empty
  const isAllEmpty = Object.values(data).every(arr => arr.length === 0);
  
  if (error && isAllEmpty) {
    return <p className="dashboard-message error">{error}</p>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Link to="/home" className="header-link">خانه</Link>
        <p className="page-title">موجودی انبار اصفهان</p>
        
        <div className="header-right-group">
          <div className="date-picker-wrapper">
            <DatePicker
              value={`${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`}
              onChange={handleDateChange}
              calendar={persian} 
              locale={persian_fa} 
              calendarPosition="bottom-right" 
              inputClass="custom-date-input" 
            />
          </div>
        </div>
      </div>

      {error && <p className="dashboard-message error-inline">{error}</p>}

      {/* Render All Product Sections */}
      {PRODUCT_TYPES.map((typeDef) => {
          const typeKey = typeDef.key;
          const processed = allProcessedData[typeKey];
          // Check if there is data to show
          const hasData = processed.sortedSizes.length > 0;
          
          const isFactoryGrouped = ['profil', 'varagh'].includes(typeKey);

          return (
            <section key={typeKey}>
                <h2 className="section-title">{typeDef.label}</h2>
                
                {!hasData ? (
                    <p className="dashboard-message">هیچ کالایی برای تاریخ انتخاب شده یافت نشد.</p>
                ) : (
                    <div className="grid-container">
                        {processed.sortedSizes.map((groupKey) => {
                            // --- تغییرات اینجا اعمال شد ---
                            let displayTitle;
                            if (isFactoryGrouped) {
                                displayTitle = `کارخانه: ${groupKey}`;
                            } else {
                                // اگر بدون سایز بود، فقط خودش را بنویس، در غیر این صورت پیشوند سایز بگذار
                                displayTitle = groupKey === "بدون سایز" 
                                    ? "بدون سایز" 
                                    : `سایز: ${toFarsiDigits(groupKey)}`;
                            }
                            // ---------------------------

                            return (
                                <Table 
                                    key={`${typeKey}-${groupKey}`}
                                    title={displayTitle}
                                    products={processed.productsBySize[groupKey]}
                                    // Pass click handler only if clickable
                                    onTitleClick={
                                        processed.sizeClickability[groupKey]
                                        ? () => handleTitleClick(typeKey, groupKey)
                                        : undefined
                                    } 
                                    isUnpricedShown={sizesShowingUnpriced.has(`${typeKey}-${groupKey}`)}
                                />
                            );
                        })}
                    </div>
                )}
            </section>
          );
      })}
      
      {/* Floating Footer */}
      <div className="floating-footer">
        <p className="pill">
          سکه: {" "}
          {lastCurrency
            ? toFarsiDigits(
                (lastCurrency.gold_coin_price / 10).toLocaleString()
              )
            : "..."}
        </p>
        <p className="pill">
          دلار: {" "}
          {lastCurrency
            ? toFarsiDigits((lastCurrency.dollar_price / 10).toLocaleString())
            : "..."}
        </p>
        <p className="pill">
          طلا: {" "}
          {lastCurrency
            ? toFarsiDigits(
                (lastCurrency.gold_gram_price / 10).toLocaleString()
              )
            : "..."}
        </p>
        <p className="pill">
          بورس: {" "}
          {lastCurrency
            ? toFarsiDigits(lastCurrency.stock_index.toLocaleString())
            : "..."}
        </p>
        <p className="pill pill-ons">
          انس:{" "}
          {lastCurrency && lastCurrency.ounce_price
            ? toFarsiDigits(lastCurrency.ounce_price.toLocaleString())
            : "..."}
        </p>
      </div>
    </div>
  );
};

export default MiscellaneousInventory;